import DesktopBar from '@dashboard/common/components/widgets/DesktopBar'
import DesktopDialogIntro from '@dashboard/common/components/widgets/DesktopDialogIntro'
import { selectHotelWebsite } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import FileUploaderDialog from 'components/ReusableComponents/FileUploaderDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import TextArea from 'components/ReusableComponents/ManageHotel/TextArea'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import useClickOut from 'hooks/useClickOut'
import { useEffect, useRef, useState } from 'react'
import { ChromePicker } from 'react-color'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications/dist/ToastProvider'
import widgetsApi from 'services/api/widgets'

import styles from './ConfigureWidgets.module.scss'
import { ReactComponent as LinkNewTabIcon } from './linkNewTab.svg'
import placeholder from './placeholder.jpg'

function getRGB(c) {
  return parseInt(c, 16) || c
}

function getsRGB(c) {
  return getRGB(c) / 255 <= 0.03928
    ? getRGB(c) / 255 / 12.92
    : Math.pow((getRGB(c) / 255 + 0.055) / 1.055, 2.4)
}

function getLuminance(hexColor) {
  return (
    0.2126 * getsRGB(hexColor.substr(1, 2)) +
    0.7152 * getsRGB(hexColor.substr(3, 2)) +
    0.0722 * getsRGB(hexColor.substr(-2))
  )
}

function getContrast(f, b) {
  const L1 = getLuminance(f)
  const L2 = getLuminance(b)
  return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05)
}

function getTextColor(bgColor) {
  const whiteContrast = getContrast(bgColor, '#ffffff')
  const blackContrast = getContrast(bgColor, '#000000')

  return whiteContrast > blackContrast ? '#ffffff' : '#000000'
}

const ColorInput = ({ title, defaultColor = '#FFFFFF', onChange, height }) => {
  const [color, setColor] = useState(defaultColor)

  const [isColorPickerVisible, setIsColorPickerVisible] = useState(false)

  const colorPickerRef = useRef()

  const [dialogX, setDialogX] = useState()
  const [dialogY, setDialogY] = useState()

  function handleClick(e) {
    setIsColorPickerVisible(true)
    setDialogX(e.clientX)
    setDialogY(e.clientY)
  }

  useClickOut(colorPickerRef, () => setIsColorPickerVisible(false))

  return (
    <div className={styles.colorPickerHolder}>
      <p className={styles.colorPickerTitle}>{title}</p>
      <div
        className={styles.colorPreviewBox}
        style={{ backgroundColor: color, height }}
        onClick={handleClick}
      >
        <p className={styles.selectedHexColor} style={{ color: getTextColor(color) }}>
          {color?.toUpperCase()}
        </p>
      </div>
      {isColorPickerVisible && (
        <div
          className={styles.colorPickerWrapper}
          ref={colorPickerRef}
          style={{ top: dialogY, left: dialogX }}
        >
          <ChromePicker
            disableAlpha={true}
            color={color}
            onChange={({ hex }) => {
              setColor(hex)
              onChange(hex)
            }}
          />
        </div>
      )}
    </div>
  )
}

const ImageFileInput = ({
  defaultUrl,
  title,
  kind,
  backgroundSize = 'cover',
  onChange,
  minHeight,
  minWidth
}) => {
  const [isUploaderVisible, setIsUploaderVisible] = useState(false)

  const [isUploadingFile, setIsUploadingFile] = useState(false)

  const [dialogX, setDialogX] = useState()
  const [dialogY, setDialogY] = useState()

  function onData() {
    setIsUploadingFile(true)
  }

  function onUpdateFiles() {
    setIsUploadingFile(true)
  }

  function onLoadFile(res) {
    const data = JSON.parse(res)
    setIsUploadingFile(false)

    if (data.isSuccess) {
      onChange(data.result)
      setIsUploaderVisible(false)
    }
  }

  function onClickRecentFile(url) {
    onChange(url)
    setTimeout(() => setIsUploaderVisible(false), 10)
  }

  function onClickOut() {
    if (!isUploadingFile) {
      setIsUploaderVisible(false)
    }
  }

  function handleClick(e) {
    setIsUploaderVisible(true)
    setDialogX(e.clientX)
    setDialogY(e.clientY)
  }

  return (
    <div className={styles.imagePreviewHolder}>
      <p className={styles.imageTitle}>{title}</p>
      <div
        className={styles.imagePreviewBox}
        style={{
          backgroundImage: `url(${defaultUrl || placeholder})`,
          backgroundColor: '#292121',
          backgroundSize
        }}
        onClick={handleClick}
      ></div>
      {isUploaderVisible && (
        <FileUploaderDialog
          onClickout={onClickOut}
          onLoad={onLoadFile}
          onUpdateFiles={onUpdateFiles}
          onClickRecentFile={onClickRecentFile}
          onData={onData}
          kind={kind}
          allowImageValidateSize={true}
          imageValidateSizeMinHeight={minHeight}
          imageValidateSizeMinWidth={minWidth}
          top={dialogY}
          left={dialogX}
        />
      )}
    </div>
  )
}

export const widgetNames = {
  TopBar: 'Top Bar',
  BottomBar: 'Bottom Bar',
  InstantPopup: 'Instant Popup',
  BeforeYouLeavePopup: 'Before You Leave Popup'
}

const widgetProperties = {
  InstantPopup: {
    popupTitle: {
      name: 'Pop-up title',
      type: 'text',
      value: 'Sign up for texts to receive offers, deals & stay in the know!'
    },
    popupDescription: {
      name: 'Pop-up description',
      type: 'longText',
      value:
        'Pellentesque in massa sem in nec dui. Dui quisque enim ut mauris tristique massa lectus.'
    },
    popupButtonText: {
      name: 'Pop-up button text',
      type: 'text',
      value: 'Get 20% discount now'
    },
    pagesToHide: []
  },
  BeforeYouLeavePopup: {
    popupTitle: {
      name: 'Pop-up title',
      type: 'text',
      value: 'Sign up for texts to receive offers, deals & stay in the know!'
    },
    popupDescription: {
      name: 'Pop-up description',
      type: 'longText',
      value:
        'Pellentesque in massa sem in nec dui. Dui quisque enim ut mauris tristique massa lectus.'
    },
    popupButtonText: {
      name: 'Pop-up button text',
      type: 'text',
      value: 'Get 20% discount now'
    },
    pagesToHide: []
  },
  TopBar: {
    barTitle: {
      name: 'Bar title',
      type: 'text',
      value: 'Get up to 20% discount lorem ipsum '
    },
    barButtonText: {
      name: 'Bar button text',
      type: 'text',
      value: 'Get the discount'
    },
    backgroundColor: {
      name: 'Bar background color',
      type: 'color',
      value: '#FFFFFF'
    },
    barButtonColor: {
      name: 'Bar button color',
      type: 'color',
      value: '#0079ff'
    },
    popupTitle: {
      name: 'Pop-up title',
      type: 'text',
      value: 'Sign up for texts to receive offers, deals & stay in the know!'
    },
    popupDescription: {
      name: 'Pop-up description',
      type: 'longText',
      value:
        'Pellentesque in massa sem in nec dui. Dui quisque enim ut mauris tristique massa lectus.'
    },
    popupButtonText: {
      name: 'Pop-up button text',
      type: 'text',
      value: 'Get 20% discount now'
    },
    pagesToHide: []
  },
  BottomBar: {
    barTitle: {
      name: 'Bar title',
      type: 'text',
      value: 'Get up to 20% discount lorem ipsum '
    },
    barButtonText: {
      name: 'Bar button text',
      type: 'text',
      value: 'Get the discount'
    },
    backgroundColor: {
      name: 'Bar background color',
      type: 'color',
      value: '#FFFFFF'
    },
    barButtonColor: {
      name: 'Bar button color',
      type: 'color',
      value: '#0079ff'
    },
    popupTitle: {
      name: 'Pop-up title',
      type: 'text',
      value: 'Sign up for texts to receive offers, deals & stay in the know!'
    },
    popupDescription: {
      name: 'Pop-up description',
      type: 'longText',
      value:
        'Pellentesque in massa sem in nec dui. Dui quisque enim ut mauris tristique massa lectus.'
    },
    popupButtonText: {
      name: 'Pop-up button text',
      type: 'text',
      value: 'Get 20% discount now'
    },
    pagesToHide: []
  }
}

const SingleWidgetConfig = ({
  widgetType,
  onSave,
  onClose,
  widgetsData,
  hotelName,
  textMarketingUrlKey
}) => {
  const [data, setData] = useState({
    ...widgetProperties[widgetType],
    ...widgetsData.find(w => w.kind === widgetType)?.data,
    hotelName,
    textMarketingUrlKey
  })

  const [isSavingDraft, setIsSavingDraft] = useState(false)

  const websiteUrl = useSelector(selectHotelWebsite)

  function saveDraftAndGoToWebsite() {
    setIsSavingDraft(true)
    widgetsApi.list
      .post({
        isDraft: true,
        data: widgetsData.map(w => ({
          ...w,
          data: w.kind === widgetType ? JSON.stringify(data) : JSON.stringify(w.data)
        }))
      })
      .then(() => {
        setIsSavingDraft(false)
        const landingPageUrl = websiteUrl

        const url = new URL(landingPageUrl)
        url.searchParams.append('widgetPreviewMode', 'true')
        window.open(url.href, '_blank')
      })
  }

  return (
    <>
      <Dialog>
        <div className={styles.dialogWrapper}>
          <div className={styles.dialog}>
            <div className={styles.dialogContent}>
              <div className={styles.singleWidgetForm}>
                {(widgetType === 'TopBar' || widgetType === 'BottomBar') && (
                  <div className={styles.section}>
                    <p className={styles.sectionTitle}>{widgetNames[widgetType]}</p>
                    <div className={styles.inputHolder}>
                      <TextInput
                        label={'Title'}
                        className={styles.input}
                        value={data.barTitle.value}
                        onChange={e =>
                          setData({
                            ...data,
                            barTitle: { ...data.barTitle, value: e.target.value }
                          })
                        }
                      />
                    </div>
                    <div className={styles.inputHolder}>
                      <TextInput
                        label={'Button text'}
                        className={styles.input}
                        value={data.barButtonText.value}
                        onChange={e =>
                          setData({
                            ...data,
                            barButtonText: {
                              ...data.barButtonText,
                              value: e.target.value
                            }
                          })
                        }
                      />
                    </div>
                    <div className={styles.colorInputHolder}>
                      <ColorInput
                        title={data.backgroundColor.name}
                        defaultColor={data.backgroundColor.value}
                        height={40}
                        onChange={color =>
                          setData({
                            ...data,
                            backgroundColor: { ...data.backgroundColor, value: color }
                          })
                        }
                      />
                    </div>
                    <div className={styles.colorInputHolder}>
                      <ColorInput
                        title={data.barButtonColor.name}
                        defaultColor={data.barButtonColor.value}
                        height={40}
                        onChange={color =>
                          setData({
                            ...data,
                            barButtonColor: { ...data.barButtonColor, value: color }
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.dialogFooter}>
              <SecondaryRegularButton onClick={() => onClose()}>
                Discard
              </SecondaryRegularButton>
              <PrimaryRegularButton onClick={() => onSave(widgetType, data)}>
                Save changes
              </PrimaryRegularButton>
            </div>
          </div>
          <div className={styles.previewBox}>
            <div className={styles.previewHeader}>
              <p className={styles.previewTitle}>Preview</p>
              {/* <button className={styles.previewButton} onClick={saveDraftAndGoToWebsite}>
                {isSavingDraft ? (
                  <>
                    {' '}
                    <LinkNewTabIcon />
                    Generating preview...
                  </>
                ) : (
                  <>
                    <LinkNewTabIcon />
                    Preview on your website
                  </>
                )}
              </button> */}
            </div>
            <>
              <div>
                {widgetType === 'TopBar' && (
                  <div className={styles.previewTopBarHolder}>
                    <DesktopBar data={data} />
                  </div>
                )}
              </div>
              {widgetType === 'BottomBar' && (
                <div className={styles.previewBottomBarHolder}>
                  <DesktopBar data={data} />
                </div>
              )}
              {!widgetType && (
                <DesktopDialogIntro
                  data={data}
                  withOverlay={false}
                  isPhoneNumberValid={true}
                />
              )}
            </>
          </div>
        </div>
      </Dialog>
    </>
  )
}

const ConfigureWidgets = ({ onClose, widgetKind, hotelName, textMarketingUrlKey }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isSavingDraft, setIsSavingDraft] = useState(false)

  const { addToast } = useToasts()

  const [widgets, setWidgets] = useState(null)

  const backgroundImageUrl = widgets?.[0]?.data?.backgroundImageUrl

  const logoUrl = widgets?.[0]?.data?.logoUrl

  const brandColor = widgets?.[0].data?.brandColor

  const popupTitle = widgets?.[0]?.data?.popupTitle.value

  const popupDescription = widgets?.[0]?.data?.popupDescription.value

  const popupButtonText = widgets?.[0]?.data?.popupButtonText.value

  const websiteUrl = useSelector(selectHotelWebsite)

  function setBackgroundImageUrl(backgroundImageUrl) {
    setWidgets(widgets.map(w => ({ ...w, data: { ...w.data, backgroundImageUrl } })))
  }

  function setLogoUrl(logoUrl) {
    setWidgets(widgets.map(w => ({ ...w, data: { ...w.data, logoUrl } })))
  }

  function setBrandColor(brandColor) {
    setWidgets(widgets.map(w => ({ ...w, data: { ...w.data, brandColor } })))
  }

  function setPopupTitle(popupTitle) {
    setWidgets(
      widgets.map(w => ({
        ...w,
        data: { ...w.data, popupTitle: { ...w.data.popupTitle, value: popupTitle } }
      }))
    )
  }

  function setPopupDescription(popupDescription) {
    setWidgets(
      widgets.map(w => ({
        ...w,
        data: {
          ...w.data,
          popupDescription: { ...w.data.popupDescription, value: popupDescription }
        }
      }))
    )
  }

  function setPopupButtonText(popupButtonText) {
    setWidgets(
      widgets.map(w => ({
        ...w,
        data: {
          ...w.data,
          popupButtonText: { ...w.data.popupButtonText, value: popupButtonText }
        }
      }))
    )
  }

  function onSaveWidgetData(widgetType, data) {
    setIsLoading(true)
    widgetsApi.list
      .post({
        data: widgets
          .map(w => (w.kind === widgetType ? { ...w, data } : w))
          .map(w => ({ ...w, data: JSON.stringify(w.data) }))
      })
      .then(() => {
        onClose()
        addToast('Widgets loaded successfully', { appearance: 'success' })
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  function saveDraftAndGoToWebsite() {
    setIsSavingDraft(true)
    widgetsApi.list
      .post({
        isDraft: true,
        data: widgets.map(w => ({ ...w, data: JSON.stringify(w.data) }))
      })
      .then(() => {
        setIsSavingDraft(false)
        const landingPageUrl = websiteUrl

        const url = new URL(landingPageUrl)
        url.searchParams.append('widgetPreviewMode', 'true')
        window.open(url.href, '_blank')
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }
  function saveChanges() {
    setIsLoading(true)
    widgetsApi.list
      .post({ data: widgets.map(w => ({ ...w, data: JSON.stringify(w.data) })) })
      .then(() => {
        onClose()
        addToast('Widgets loaded successfully', { appearance: 'success' })
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    setIsLoading(true)

    widgetsApi.list
      .get()
      .then(response => {
        setWidgets(response.map(w => ({ ...w, data: JSON.parse(w.data) })))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [addToast])

  if (isLoading) {
    return (
      <Dialog>
        <LoadingIndicator white />
      </Dialog>
    )
  }

  if (widgetKind) {
    return (
      <SingleWidgetConfig
        widgetType={widgetKind}
        widgetsData={widgets}
        defaultData={widgets.find(w => w.kind === widgetKind)?.data}
        onClose={onClose}
        onSave={onSaveWidgetData}
        textMarketingUrlKey={textMarketingUrlKey}
        hotelName={hotelName}
      />
    )
  }

  return (
    <Dialog>
      <div className={styles.dialogWrapper}>
        <div className={styles.dialog}>
          <div className={styles.dialogHeader}>
            <div className={styles.dialogTitle}>Customize offer dialog</div>
          </div>
          <div className={styles.dialogContent}>
            <div className={styles.brandConfig}>
              <div className={styles.brandOptions}>
                <ImageFileInput
                  kind="Background"
                  title="Background image"
                  defaultUrl={backgroundImageUrl}
                  onChange={setBackgroundImageUrl}
                  minWidth={380}
                  minHeight={600}
                />
                <ImageFileInput
                  kind="Logo"
                  title="Logo"
                  backgroundSize="90%"
                  defaultUrl={logoUrl}
                  onChange={setLogoUrl}
                  minWidth={200}
                  minHeight={200}
                />
                <ColorInput
                  title="Brand color"
                  defaultColor={brandColor}
                  onChange={setBrandColor}
                />
              </div>
            </div>
            <div className={styles.singleWidgetForm}>
              <div className={styles.section}>
                <div className={styles.inputHolder}>
                  <TextInput
                    label={'Title'}
                    className={styles.input}
                    value={popupTitle}
                    onChange={e => setPopupTitle(e.target.value)}
                  />
                </div>
                <div className={styles.inputHolder}>
                  <TextArea
                    label="Description"
                    className={styles.input}
                    value={popupDescription}
                    onChange={e => setPopupDescription(e.target.value)}
                  />
                </div>
                <div className={styles.inputHolder}>
                  <TextInput
                    label="Button text"
                    className={styles.input}
                    value={popupButtonText}
                    onChange={e => setPopupButtonText(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.dialogFooter}>
            <SecondaryRegularButton onClick={onClose}>Discard</SecondaryRegularButton>
            <PrimaryRegularButton onClick={saveChanges}>
              Save and publish
            </PrimaryRegularButton>
          </div>
        </div>
        <div className={styles.previewBox}>
          <div className={styles.previewHeader}>
            <p className={styles.previewTitle}>Preview</p>
            {/* <button className={styles.previewButton} onClick={saveDraftAndGoToWebsite}>
              {isSavingDraft ? (
                <>
                  {' '}
                  <LinkNewTabIcon />
                  Generating preview...
                </>
              ) : (
                <>
                  <LinkNewTabIcon />
                  Preview on your website
                </>
              )}
            </button> */}
          </div>
          <DesktopDialogIntro
            data={widgets.find(w => w.kind === 'TopBar').data}
            withOverlay={false}
            isPhoneNumberValid={true}
          />
        </div>
      </div>
    </Dialog>
  )
}

export default ConfigureWidgets
